import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API } from '../../App';

export const CallMessagesTab = ({
  callSettings,
  uploadingCall,
  updatingCall,
  needsAttentionLabel,
  setNeedsAttentionLabel,
  customMessageLabel,
  setCustomMessageLabel,
  customMessageText,
  setCustomMessageText,
  button3Label,
  setButton3Label,
  button4Label,
  setButton4Label,
  onUploadNeedsAttentionAudio,
  onUploadButton3Audio,
  onUploadButton4Audio,
  onUpdateCallMessage,
  onPlaySound
}) => {
  const playAudio = (audioUrl) => {
    if (!audioUrl) return;
    try {
      const url = audioUrl.startsWith('/api') 
        ? `${API.replace('/api', '')}${audioUrl}` 
        : `${API}${audioUrl}`;
      const audio = new Audio(url);
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => console.warn('Audio playback failed:', err.message));
      }
    } catch (err) {
      console.warn('Audio initialization failed:', err.message);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-3xl text-red-600 mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '4px', fontWeight: '400' }}>
        MANAGE CALL MESSAGES
      </h2>
      <div className="mb-6 p-4 bg-blue-50 border-4 border-blue-400 rounded">
        <p className="text-sm text-gray-700 font-bold">ℹ️ Configure automated phone call messages for review alerts.</p>
      </div>

      <div className="grid gap-6">
        {/* Button 1 */}
        <div className="bg-white p-6 rounded border-4 border-black">
          <h3 className="text-xl font-bold text-red-600 mb-3" style={{ letterSpacing: '1px' }}>🚨 BUTTON 1: NEEDS ATTENTION</h3>
          <div className="mb-4">
            <Label className="text-gray-700 font-bold mb-2 block">Button Label:</Label>
            <Input 
              value={needsAttentionLabel} 
              onChange={(e) => setNeedsAttentionLabel(e.target.value)} 
              className="max-w-md border-2 border-black" 
            />
          </div>
          <div className="mb-4">
            <Label className="text-gray-700 font-bold mb-2 block">Audio File (.mp3):</Label>
            <p className="text-sm text-gray-600 mb-3">Current: {callSettings?.needs_attention_audio || 'No audio uploaded'}</p>
            <div className="flex gap-3 flex-wrap">
              <Label htmlFor="needs-attention-upload" className="cursor-pointer">
                <div className="px-4 py-2 bg-red-500 text-white rounded border-2 border-black font-bold hover:bg-red-600 inline-block">
                  {uploadingCall === 'needs_attention' ? 'Uploading...' : 'Upload Audio File'}
                </div>
                <Input 
                  id="needs-attention-upload" 
                  type="file" 
                  accept=".mp3,audio/mpeg" 
                  className="hidden"
                  onChange={(e) => e.target.files[0] && onUploadNeedsAttentionAudio(e.target.files[0])}
                  disabled={uploadingCall === 'needs_attention'} 
                />
              </Label>
              {callSettings?.needs_attention_audio && (
                <Button 
                  onClick={() => onPlaySound(callSettings.needs_attention_audio)} 
                  className="bg-green-500 hover:bg-green-600 text-white border-2 border-black"
                >
                  🔊 Test Audio
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Button 2 */}
        <div className="bg-white p-6 rounded border-4 border-black">
          <h3 className="text-xl font-bold text-blue-600 mb-3" style={{ letterSpacing: '1px' }}>📞 BUTTON 2: CUSTOM MESSAGE</h3>
          <div className="mb-4">
            <Label className="text-gray-700 font-bold mb-2 block">Button Label:</Label>
            <Input 
              value={customMessageLabel} 
              onChange={(e) => setCustomMessageLabel(e.target.value)} 
              className="max-w-md border-2 border-black" 
            />
          </div>
          <div className="mb-4">
            <Label className="text-gray-700 font-bold mb-2 block">Message Text:</Label>
            <Textarea 
              value={customMessageText} 
              onChange={(e) => setCustomMessageText(e.target.value)}
              placeholder="Enter message text..." 
              className="min-h-[120px] border-2 border-black" 
              maxLength={1000} 
            />
            <p className="text-xs text-gray-500 mt-1">{customMessageText.length}/1000 characters</p>
          </div>
          <Button 
            onClick={() => onUpdateCallMessage('custom')} 
            disabled={updatingCall === 'custom'}
            className="bg-blue-500 hover:bg-blue-600 text-white border-2 border-black font-bold"
          >
            {updatingCall === 'custom' ? 'Updating...' : '💾 Save Message'}
          </Button>
        </div>

        {/* Button 3 */}
        <div className="bg-green-50 p-4 rounded border-4 border-green-400">
          <h3 className="text-xl font-bold text-green-700 mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>🔔 Button 3: Custom Audio Alert</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-bold">Button Label</Label>
              <Input 
                type="text" 
                value={button3Label} 
                onChange={(e) => setButton3Label(e.target.value)} 
                className="mt-1 border-2 border-black" 
              />
            </div>
            <div>
              <Label className="text-sm font-bold">Upload Audio File (.mp3)</Label>
              <Input 
                type="file" 
                accept=".mp3" 
                onChange={(e) => e.target.files[0] && onUploadButton3Audio(e.target.files[0])}
                disabled={uploadingCall === 'button3'} 
                className="mt-1 border-2 border-black" 
              />
            </div>
            {callSettings?.button3_audio && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => playAudio(callSettings.button3_audio)} 
                className="border-2 border-black"
              >
                🔊 Test Audio
              </Button>
            )}
          </div>
        </div>

        {/* Button 4 */}
        <div className="bg-purple-50 p-4 rounded border-4 border-purple-400">
          <h3 className="text-xl font-bold text-purple-700 mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>📞 Button 4: Custom Audio Alert</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-bold">Button Label</Label>
              <Input 
                type="text" 
                value={button4Label} 
                onChange={(e) => setButton4Label(e.target.value)} 
                className="mt-1 border-2 border-black" 
              />
            </div>
            <div>
              <Label className="text-sm font-bold">Upload Audio File (.mp3)</Label>
              <Input 
                type="file" 
                accept=".mp3" 
                onChange={(e) => e.target.files[0] && onUploadButton4Audio(e.target.files[0])}
                disabled={uploadingCall === 'button4'} 
                className="mt-1 border-2 border-black" 
              />
            </div>
            {callSettings?.button4_audio && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => playAudio(callSettings.button4_audio)} 
                className="border-2 border-black"
              >
                🔊 Test Audio
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-yellow-50 border-4 border-yellow-400 rounded">
        <p className="text-sm text-gray-700 font-bold">💡 Buttons 1, 3 & 4: Upload .mp3 files under 5MB. Button 2: Use TTS (max 1000 chars).</p>
      </div>
    </div>
  );
};
